/**
 * Streamer — painel do criador de conteúdo (player-side).
 * Endpoints do streamer logado (/api/streamers/me/*) + vitrine pública
 * (/api/streamers/public). Todos os /me/* exigem que a conta SEJA um streamer
 * (o backend devolve 404 se não for).
 *
 * Reutiliza os helpers compartilhados de api.ts (mesma fonte que o adminService).
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './api';

/* ───────────────────────────── Tipos ───────────────────────────── */

export type StreamerPlatform =
  | 'twitch' | 'youtube' | 'kick' | 'tiktok'
  | 'instagram' | 'x' | 'twitter' | 'discord' | 'facebook';

export interface StreamerSocial {
  id: number;
  platform: string;
  url: string;
}

export interface StreamerConnection {
  id: number;
  platform: string;       // twitch | youtube | kick | tiktok
  channelId: string;
  channelUrl: string;
  verified: boolean;
  isPrimary: boolean;
}

export interface StreamerCoupon {
  id: number;
  code: string;
  type: string;           // 'fixo' | 'live' | ...
  cashbackPct: number;
  expiresAt: string;
}

export interface StreamerSession {
  id: number;
  platform: string;
  startAt: string;
  endAt?: string | null;
  durationMin?: number | null;
  verified?: boolean | null;
}

export interface StreamerMetrics {
  recargasMesBrl: number;
  comissaoGeradaBrl: number;
  sessoesVerificadas: number;
}

export interface StreamerMe {
  id: number;
  userNum: number;
  nick: string;
  status: string;
  avatar: string | null;
  bio: string | null;
  commissionPct: number;
  createdAt: string;
  metrics: StreamerMetrics;
  socials: StreamerSocial[];
  connections: StreamerConnection[];
  activeCoupon: StreamerCoupon | null;
  activeSession: { id: number; platform: string; startAt: string } | null;
}

export interface StreamerProfileUpdate {
  nick?: string;
  avatar?: string;
  bio?: string;
}

export interface CouponsView {
  activeCoupon: StreamerCoupon | null;
  tier?: string | null;
  [key: string]: unknown;
}

export interface CommissionItem {
  brl: number;
  commissionBrl: number;
  commissionStatus: string;
  createdAt: string;
}

export interface CommissionsView {
  items: CommissionItem[];
  totals: {
    brl?: number;
    commissionBrl?: number;
    pendingBrl?: number;
    paidBrl?: number;
    [key: string]: number | undefined;
  };
}

export interface PublicStreamer {
  id: number;
  nick: string;
  avatar: string | null;
  platform: string;
  channelUrl: string;
  channelId?: string | null;
  videoId?: string | null;
  live: boolean;
  viewers: number | null;
}

/* ───────────────────────── Perfil (me) ─────────────────────────── */

/** GET /api/streamers/me — perfil completo do streamer logado (404 se não for streamer).
 *  O backend responde no formato { streamer, socials, connections, activeCoupon,
 *  activeSession }; aqui achatamos pro StreamerMe que a UI consome. */
export const getStreamerMe = async (): Promise<StreamerMe> => {
  const r = await apiGet<any>('/streamers/me');
  const s = r?.streamer ?? r ?? {};
  return {
    id: s.id,
    userNum: s.userNum,
    nick: s.nick ?? '',
    status: s.status ?? '',
    avatar: s.avatar ?? null,
    bio: s.bio ?? null,
    commissionPct: s.commissionPct ?? 0,
    createdAt: s.createdAt ?? '',
    metrics: {
      recargasMesBrl: s.rechargesBrlMonth ?? 0,
      comissaoGeradaBrl: s.commissionBrlMonth ?? 0,
      sessoesVerificadas: s.verifiedSessions ?? 0,
    },
    socials: r?.socials ?? [],
    connections: r?.connections ?? [],
    activeCoupon: r?.activeCoupon ?? null,
    activeSession: r?.activeSession
      ? { id: r.activeSession.id, platform: r.activeSession.platform, startAt: r.activeSession.startAt }
      : null,
  };
};

/** PATCH /api/streamers/me — edita nick/avatar/bio. */
export const updateStreamerMe = (input: StreamerProfileUpdate) =>
  apiPatch<StreamerMe>('/streamers/me', input);

/* ───────────────────────── Redes sociais ──────────────────────── */

export const getSocials = () =>
  apiGet<StreamerSocial[]>('/streamers/me/socials');

/** platform é opcional: o backend auto-detecta pelo domínio da URL. */
export const addSocial = (url: string, platform?: string) =>
  apiPost<StreamerSocial>('/streamers/me/socials', { url, platform });

export const deleteSocial = (id: number) =>
  apiDelete<{ status: string; message?: string }>(`/streamers/me/socials/${id}`);

/* ───────────────────────── Conexões (canais) ──────────────────── */

export const getConnections = () =>
  apiGet<StreamerConnection[]>('/streamers/me/connections');

export const addConnection = (platform: string, channelId: string, channelUrl: string) =>
  apiPost<StreamerConnection>('/streamers/me/connections', { platform, channelId, channelUrl });

export const deleteConnection = (id: number) =>
  apiDelete<{ status: string; message?: string }>(`/streamers/me/connections/${id}`);

/* ───────────────────────────── Cupons ─────────────────────────── */

export const getMyCoupons = () =>
  apiGet<CouponsView>('/streamers/me/coupons');

/** Gera cupom fixo (5% / 5 dias). Rejeita se já houver um ativo. */
export const generateFixedCoupon = () =>
  apiPost<StreamerCoupon>('/streamers/me/coupons/fixo/gerar', {});

export const getFixedHistory = () =>
  apiGet<StreamerCoupon[]>('/streamers/me/coupons/fixo/historico');

/* ───────────────────────────── Comissões ──────────────────────── */

export const getMyCommissions = async (status?: string): Promise<CommissionsView> => {
  // A UI usa status em inglês (pending/paid); o backend usa PT (pendente/pago).
  const stMap: Record<string, string> = { pending: 'pendente', paid: 'pago', cancelled: 'cancelado' };
  const st = status ? (stMap[status] ?? status) : undefined;
  const q = st ? `?status=${encodeURIComponent(st)}` : '';
  const r = await apiGet<any>(`/streamers/me/commissions${q}`);
  const items = (r?.items ?? []).map((it: any) => ({
    brl: it.brl ?? 0,
    commissionBrl: it.commissionBrl ?? 0,
    // Traduz PT->EN pro badge da UI.
    commissionStatus:
      it.commissionStatus === 'pago' ? 'paid'
      : it.commissionStatus === 'pendente' ? 'pending'
      : it.commissionStatus === 'cancelado' ? 'cancelled'
      : (it.commissionStatus ?? ''),
    createdAt: it.createdAt ?? '',
  }));
  const paidBrl = r?.totalPaid ?? 0;
  const pendingBrl = r?.totalPending ?? 0;
  return {
    items,
    totals: {
      brl: items.reduce((a: number, it: any) => a + (it.brl || 0), 0),
      commissionBrl: paidBrl + pendingBrl,
      pendingBrl,
      paidBrl,
    },
  };
};

/* ───────────────────────────── Sessões live ───────────────────── */

export const getMySessions = async (): Promise<StreamerSession[]> => {
  const rows = await apiGet<any[]>('/streamers/me/sessions');
  return (rows ?? []).map((s) => ({
    id: s.id,
    platform: s.platform,
    startAt: s.startAt,
    endAt: s.endAt ?? null,
    durationMin: s.durationSec != null ? Math.round(s.durationSec / 60) : null,
    verified: !!s.validated,
  }));
};

export const startLive = (platform: string) =>
  apiPost<StreamerSession>('/streamers/me/sessions/start', { platform });

export const stopLive = () =>
  apiPost<{ status: string; message?: string }>('/streamers/me/sessions/stop', {});

/* ───────────────────────────── Público ────────────────────────── */

/** GET /api/streamers/public — vitrine (não exige auth de streamer). */
export const getPublicStreamers = () =>
  apiGet<PublicStreamer[]>('/streamers/public');
